import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { useSimpleToast, getErrorMessage } from '../../components/Toast';
import { Search, Filter, Eye, UserPlus, Phone, Mail, Calendar, Briefcase, Users, CheckCircle, Clock, Edit2, Trash2, UserCheck, UserX, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function Candidates({ user, onLogout }) {
  const { showToast, ToastComponent } = useSimpleToast();
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedStage, setSelectedStage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [viewCandidate, setViewCandidate] = useState(null);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    job_id: '',
    resume_url: '',
    cover_letter: '',
    experience_years: 0,
    current_ctc: 0,
    expected_ctc: 0,
    notice_period: 30
  });

  const stages = ['Applied', 'Screened', 'Interview Scheduled', 'Selected', 'Rejected', 'Offer Issued', 'Offer Accepted'];

  useEffect(() => {
    fetchJobs();
    fetchCandidates();
  }, [selectedJob, selectedStage]);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/recruitment/jobs?status=Active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (selectedJob) params.job_id = selectedJob;
      if (selectedStage) params.stage = selectedStage;
      
      const response = await axios.get(`${API}/api/recruitment/candidates`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingCandidate) {
      return handleUpdateCandidate(e);
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/recruitment/candidates`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Candidate added successfully!', 'success');
      setShowAddForm(false);
      fetchCandidates();
      resetForm();
    } catch (error) {
      console.error('Add candidate error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      job_id: '',
      resume_url: '',
      cover_letter: '',
      experience_years: 0,
      current_ctc: 0,
      expected_ctc: 0,
      notice_period: 30
    });
    setEditingCandidate(null);
  };

  const handleEditCandidate = (candidate) => {
    setEditingCandidate(candidate);
    setFormData({
      name: candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
      email: candidate.email || '',
      phone: candidate.phone || candidate.mobile || '',
      job_id: candidate.job_id || '',
      resume_url: candidate.resume_url || '',
      cover_letter: candidate.cover_letter || '',
      experience_years: candidate.experience_years || candidate.experience || 0,
      current_ctc: candidate.current_ctc || 0,
      expected_ctc: candidate.expected_ctc || 0,
      notice_period: candidate.notice_period || 30
    });
    setShowAddForm(true);
  };

  const handleUpdateCandidate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/candidates/${editingCandidate.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Candidate updated successfully!', 'success');
      setShowAddForm(false);
      fetchCandidates();
      resetForm();
    } catch (error) {
      console.error('Update candidate error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/candidates/${candidateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Candidate deleted successfully!', 'success');
      fetchCandidates();
    } catch (error) {
      console.error('Delete candidate error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const handleStageChange = async (candidateId, newStage) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/api/candidates/${candidateId}/stage`, 
        { stage: newStage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(`Candidate stage updated to ${newStage}!`, 'success');
      fetchCandidates();
      setViewCandidate(null);
    } catch (error) {
      console.error('Stage change error:', error);
      showToast(getErrorMessage(error), 'error');
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      'Applied': '#ff9800',
      'Screened': '#2196f3',
      'Interview Scheduled': '#9c27b0',
      'Selected': '#4caf50',
      'Rejected': '#f44336',
      'Offer Issued': '#00bcd4',
      'Offer Accepted': '#4caf50'
    };
    return colors[stage] || '#757575';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <ToastComponent />
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Candidate Management</h1>
            <p className="text-gray-600 mt-2">Track and manage all job applications</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
            style={{backgroundColor: '#2b3380'}}
          >
            <UserPlus className="w-5 h-5" />
            Add Candidate
          </button>
        </div>

        {/* Stats Cards - Clean white design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8eaf6'}}>
                <Users className="w-6 h-6" style={{color: '#2b3380'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{candidates.length}</p>
                <p className="text-sm text-gray-500">Total Candidates</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e3f2fd'}}>
                <Search className="w-6 h-6" style={{color: '#2196f3'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#2196f3'}}>{candidates.filter(c => c.stage === 'Screened' || c.stage === 'Screening').length}</p>
                <p className="text-sm text-gray-500">In Screening</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#f3e5f5'}}>
                <Clock className="w-6 h-6" style={{color: '#9c27b0'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#9c27b0'}}>{candidates.filter(c => c.stage === 'Interview Scheduled' || c.stage === 'Interview Done').length}</p>
                <p className="text-sm text-gray-500">In Interview</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#e8f5e9'}}>
                <CheckCircle className="w-6 h-6" style={{color: '#4caf50'}} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{color: '#4caf50'}}>{candidates.filter(c => c.stage === 'Selected' || c.stage === 'Offer' || c.stage === 'Offer Sent' || c.stage === 'Offer Accepted').length}</p>
                <p className="text-sm text-gray-500">Selected</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Filter by Job</label>
              <select
                value={selectedJob}
                onChange={(e) => setSelectedJob(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">All Jobs</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Filter by Stage</label>
              <select
                value={selectedStage}
                onChange={(e) => setSelectedStage(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">All Stages</option>
                {stages.map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Add Candidate Form */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold mb-4" style={{color: '#2b3380'}}>
                {editingCandidate ? 'Edit Candidate' : 'Add New Candidate'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone *</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Job Position *</label>
                    <select
                      value={formData.job_id}
                      onChange={(e) => setFormData({...formData, job_id: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">Select Job</option>
                      {jobs.map(job => (
                        <option key={job.id} value={job.id}>{job.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Experience (Years)</label>
                    <input
                      type="number"
                      value={formData.experience_years}
                      onChange={(e) => setFormData({...formData, experience_years: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                      step="0.5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Current CTC (₹)</label>
                    <input
                      type="number"
                      value={formData.current_ctc}
                      onChange={(e) => setFormData({...formData, current_ctc: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Expected CTC (₹)</label>
                    <input
                      type="number"
                      value={formData.expected_ctc}
                      onChange={(e) => setFormData({...formData, expected_ctc: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Notice Period (Days)</label>
                    <input
                      type="number"
                      value={formData.notice_period}
                      onChange={(e) => setFormData({...formData, notice_period: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Resume URL</label>
                    <input
                      type="text"
                      value={formData.resume_url}
                      onChange={(e) => setFormData({...formData, resume_url: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="https://..."
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Cover Letter</label>
                    <textarea
                      value={formData.cover_letter}
                      onChange={(e) => setFormData({...formData, cover_letter: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="px-6 py-2 text-white rounded-lg"
                    style={{backgroundColor: '#2b3380'}}
                  >
                    {editingCandidate ? 'Update Candidate' : 'Add Candidate'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); resetForm(); }}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Candidate List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{backgroundColor: '#2b3380'}}>
                <tr>
                  <th className="px-6 py-3 text-left text-white">Candidate</th>
                  <th className="px-6 py-3 text-left text-white">Contact</th>
                  <th className="px-6 py-3 text-left text-white">Job Position</th>
                  <th className="px-6 py-3 text-left text-white">Experience</th>
                  <th className="px-6 py-3 text-left text-white">CTC</th>
                  <th className="px-6 py-3 text-left text-white">Stage</th>
                  <th className="px-6 py-3 text-left text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {candidates.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <span className="text-4xl mb-2">📭</span>
                        <p>No candidates found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  candidates.map((candidate) => {
                    const job = jobs.find(j => j.id === candidate.job_id);
                    const candidateName = candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unknown';
                    return (
                      <tr key={candidate.id} className="border-b hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{backgroundColor: '#2b3380'}}>
                              {candidateName.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium">{candidateName}</p>
                              <p className="text-sm text-gray-500">Applied: {new Date(candidate.created_at || candidate.applied_date).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <span>{candidate.email}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span>{candidate.phone || candidate.mobile}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Briefcase className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{job?.title || candidate.job_applied || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span>{candidate.experience_years || candidate.experience || 0} years</span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <p>Current: ₹{(candidate.current_ctc)?.toLocaleString() || 'N/A'}</p>
                            <p>Expected: ₹{candidate.expected_ctc?.toLocaleString() || 'N/A'}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className="px-3 py-1 rounded-full text-sm font-medium text-white"
                            style={{backgroundColor: getStageColor(candidate.stage)}}
                          >
                            {candidate.stage}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setViewCandidate(candidate)}
                              className="p-2 rounded-lg hover:bg-gray-100"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" style={{color: '#2b3380'}} />
                            </button>
                            <button
                              onClick={() => handleEditCandidate(candidate)}
                              className="p-2 rounded-lg hover:bg-blue-50"
                              title="Edit Candidate"
                            >
                              <Edit2 className="w-4 h-4 text-blue-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteCandidate(candidate.id)}
                              className="p-2 rounded-lg hover:bg-red-50"
                              title="Delete Candidate"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                            {candidate.stage !== 'Selected' && candidate.stage !== 'Rejected' && (
                              <>
                                <button
                                  onClick={() => handleStageChange(candidate.id, 'Shortlisted')}
                                  className="p-2 rounded-lg hover:bg-green-50"
                                  title="Shortlist"
                                >
                                  <UserCheck className="w-4 h-4 text-green-600" />
                                </button>
                                <button
                                  onClick={() => handleStageChange(candidate.id, 'Rejected')}
                                  className="p-2 rounded-lg hover:bg-red-50"
                                  title="Reject"
                                >
                                  <UserX className="w-4 h-4 text-red-600" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* View Candidate Modal */}
        {viewCandidate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold mb-4" style={{color: '#2b3380'}}>Candidate Details</h2>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="font-medium">{viewCandidate.name || `${viewCandidate.first_name || ''} ${viewCandidate.last_name || ''}`.trim()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{viewCandidate.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{viewCandidate.phone || viewCandidate.mobile}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Experience</p>
                  <p className="font-medium">{viewCandidate.experience_years || viewCandidate.experience || 0} years</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current CTC</p>
                  <p className="font-medium">₹{viewCandidate.current_ctc?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Expected CTC</p>
                  <p className="font-medium">₹{viewCandidate.expected_ctc?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Notice Period</p>
                  <p className="font-medium">{viewCandidate.notice_period}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Stage</p>
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium text-white inline-block"
                    style={{backgroundColor: getStageColor(viewCandidate.stage)}}
                  >
                    {viewCandidate.stage}
                  </span>
                </div>
              </div>
              {viewCandidate.cover_letter && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Cover Letter</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg">{viewCandidate.cover_letter}</p>
                </div>
              )}
              {viewCandidate.resume_url && (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Resume</p>
                  <a
                    href={viewCandidate.resume_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    View Resume →
                  </a>
                </div>
              )}
              
              {/* Stage Change Actions */}
              {viewCandidate.stage !== 'Selected' && viewCandidate.stage !== 'Rejected' && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium mb-3">Quick Actions</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleStageChange(viewCandidate.id, 'Screened')}
                      className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 flex items-center gap-2"
                    >
                      <ArrowRight className="w-4 h-4" /> Move to Screening
                    </button>
                    <button
                      onClick={() => handleStageChange(viewCandidate.id, 'Shortlisted')}
                      className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm hover:bg-purple-200 flex items-center gap-2"
                    >
                      <UserCheck className="w-4 h-4" /> Shortlist
                    </button>
                    <button
                      onClick={() => handleStageChange(viewCandidate.id, 'Interview Scheduled')}
                      className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg text-sm hover:bg-orange-200 flex items-center gap-2"
                    >
                      <Calendar className="w-4 h-4" /> Schedule Interview
                    </button>
                    <button
                      onClick={() => handleStageChange(viewCandidate.id, 'Selected')}
                      className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" /> Select
                    </button>
                    <button
                      onClick={() => handleStageChange(viewCandidate.id, 'Rejected')}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 flex items-center gap-2"
                    >
                      <UserX className="w-4 h-4" /> Reject
                    </button>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <button
                  onClick={() => { handleEditCandidate(viewCandidate); setViewCandidate(null); }}
                  className="px-6 py-2 text-white rounded-lg flex items-center gap-2"
                  style={{backgroundColor: '#2b3380'}}
                >
                  <Edit2 className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => setViewCandidate(null)}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Candidates;